import cookieHelper from './getCookieData';

/**
 * Helper to get current authenticated user data from cookies / localStorage
 */
export const getAuthUser = () => {
    try {
        const rawAuth = cookieHelper.getCookie('authUser');
        if (rawAuth) {
            return typeof rawAuth === 'object' ? rawAuth : JSON.parse(rawAuth);
        }
        const localAuth = localStorage.getItem('authUser');
        if (localAuth) {
            return JSON.parse(localAuth);
        }
    } catch (e) {
        console.error('Error parsing authUser:', e);
    }
    return null;
};

/**
 * Returns the standardized role name of the logged-in user:
 * 'superadmin' | 'admin' | 'doctor' | 'receptionist' | 'patient' | 'staff'
 */
export const getUserRole = () => {
    const auth = getAuthUser();
    const userObj = auth?.user || auth;
    const email = (userObj?.email || auth?.email || '').toLowerCase().trim();
    const roleId = Number(userObj?.role || userObj?.role_id || auth?.role || 0);

    // 1. Platform SuperAdmin (strictly role 99 or superadmin email)
    if (email === 'superadmin@oralstop.com' || roleId === 99) {
        return 'superadmin';
    }
    
    // 2. Check role from active authUser session
    const roleName = userObj?.user_role?.name || userObj?.role_name || (typeof userObj?.role === 'string' ? userObj.role : '') || '';
    if (roleName && typeof roleName === 'string' && roleName.trim()) {
        const lower = roleName.toLowerCase().trim();
        if (lower === 'superadmin' || lower === 'super_admin' || lower.includes('super admin')) return 'superadmin';
        if (lower.includes('admin') || lower.includes('owner') || lower.includes('director')) return 'admin';
        if (lower.includes('doctor') || lower.includes('surgeon') || lower.includes('dentist')) return 'doctor';
        if (lower.includes('reception') || lower.includes('front') || lower.includes('desk') || lower.includes('cashier')) return 'receptionist';
        if (lower.includes('patient')) return 'patient';
        return lower;
    }

    // 3. Check numeric roles
    if (roleId === 1) return 'admin';
    if (roleId === 2) return 'doctor';
    if (roleId === 3) return 'receptionist';
    if (roleId === 4) return 'patient';

    if (email.includes('patient')) {
        return 'patient';
    }

    const localRole = localStorage.getItem('role');
    if (localRole) {
        const lowerLocal = localRole.toLowerCase().trim();
        if (lowerLocal === 'superadmin') return 'superadmin';
        return lowerLocal;
    }

    return 'admin';
};

/**
 * Check if the logged-in user is strictly the SaaS SuperAdmin (Platform Owner)
 */
export const isSuperAdminUser = () => {
    const auth = getAuthUser();
    const userObj = auth?.user || auth;
    const email = (userObj?.email || auth?.email || '').toLowerCase().trim();
    const roleId = Number(userObj?.role || userObj?.role_id || auth?.role || 0);
    const roleName = String(userObj?.user_role?.name || userObj?.role_name || (typeof userObj?.role === 'string' ? userObj.role : '') || '').toLowerCase().trim();

    return email === 'superadmin@oralstop.com' || roleId === 99 || roleName === 'superadmin' || roleName === 'super_admin';
};

/**
 * Check if the logged-in user is a Clinic Admin
 */
export const isAdminUser = () => {
    const role = getUserRole();
    const auth = getAuthUser();
    const userObj = auth?.user || auth;
    const roleId = Number(userObj?.role || userObj?.role_id || auth?.role || 0);
    return (role === 'admin' || roleId === 1) && !isSuperAdminUser();
};

/**
 * Get permission map for current user
 */
export const getUserPermissions = () => {
    const auth = getAuthUser();
    const perms = auth?.user?.user_role?.role_permissions || auth?.role_permissions || [];
    return Array.isArray(perms) ? perms : [];
};

/**
 * Check granular permission for a specific module and action
 * @param {string} moduleName - e.g. 'Patients', 'Appointment', 'Accounts', 'Prescriptions'
 * @param {string} action - 'is_accessable' | 'is_readable' | 'is_creatable' | 'is_writable' | 'is_deletable'
 */
export const hasPermission = (moduleName, action = 'is_accessable') => {
    // Admin always has full access
    if (isAdminUser()) return true;

    if (!moduleName) return true;

    const permissions = getUserPermissions();
    if (!permissions || permissions.length === 0) {
        // Default role based fallbacks if explicit permissions array is not yet seeded
        const role = getUserRole();
        if (role === 'doctor') {
            const clinicalModules = ['patients', 'appointment', 'chairs', 'dentalchart', 'prescriptions', 'treatments', 'examinations', 'files', 'investigations'];
            if (clinicalModules.includes(moduleName.toLowerCase())) return true;
            if (action === 'is_readable' && ['accounts', 'billings', 'payments'].includes(moduleName.toLowerCase())) return true;
            return false;
        }
        if (role === 'receptionist') {
            const frontDeskModules = [
                'patients', 'appointment', 'chairs', 'accounts', 'billings',
                'payments', 'vouchers', 'notes', 'followup', 'followups', 'tasks'
            ];
            if (frontDeskModules.includes(moduleName.toLowerCase())) return true;
            return false;
        }
        if (role === 'patient') {
            return ['portal', 'my-appointments', 'my-treatments', 'my-bills'].includes(moduleName.toLowerCase());
        }
        return true;
    }

    const mod = permissions.find(
        p => (p.module_name || p.name || '').toLowerCase() === moduleName.toLowerCase()
    );

    if (!mod) return false;

    return Boolean(mod[action] || mod.is_accessable);
};

export const canRead = (moduleName) => hasPermission(moduleName, 'is_readable');
export const canCreate = (moduleName) => hasPermission(moduleName, 'is_creatable');
export const canUpdate = (moduleName) => hasPermission(moduleName, 'is_writable');
export const canDelete = (moduleName) => hasPermission(moduleName, 'is_deletable');

/**
 * Detects the logged-in doctor option from a list of doctor dropdown options [{ value, label, email, ... }]
 * If the current authenticated user is a Doctor or matches any doctor in the clinic:
 * 1. Matches by email (case-insensitive)
 * 2. Matches by doctor code / user id / value
 * 3. Matches by doctor name / label (case-insensitive, ignoring "Dr.", "Dr ", etc.)
 * 4. If user's role is doctor and only 1 doctor is present or default fallback
 */
export const getLoggedInDoctorOption = (doctors = []) => {
    if (!Array.isArray(doctors) || doctors.length === 0) return null;

    const auth = getAuthUser();
    const userObj = auth?.user || auth || {};
    const role = getUserRole();

    const userEmail = (userObj?.email || auth?.email || '').toLowerCase().trim();
    const userName = (userObj?.user_name || userObj?.name || auth?.user_name || auth?.name || '').toLowerCase().trim();
    const userDocCode = String(userObj?.doctor_code || userObj?.code || auth?.doctor_code || auth?.code || '').toLowerCase().trim();
    const userId = String(userObj?.id || userObj?.user_id || auth?.id || auth?.user_id || '').trim();

    // 1. Direct match by email
    if (userEmail) {
        const byEmail = doctors.find(d => d.email && String(d.email).toLowerCase().trim() === userEmail);
        if (byEmail) return byEmail;
    }

    // 2. Direct match by doctor_code / value
    if (userDocCode) {
        const byCode = doctors.find(d => String(d.value).toLowerCase().trim() === userDocCode || String(d.code || '').toLowerCase().trim() === userDocCode);
        if (byCode) return byCode;
    }

    // 3. Direct match by user id
    if (userId) {
        const byId = doctors.find(d => String(d.value).trim() === userId || String(d.id || '').trim() === userId);
        if (byId) return byId;
    }

    // 4. Match by Name (strip 'Dr.', 'Dr ', 'Doctor', etc.)
    if (userName) {
        const normalize = (str) => String(str || '').toLowerCase().replace(/^(dr\.?|doctor)\s+/i, '').replace(/[^a-z0-9]/g, '');
        const cleanUserName = normalize(userName);
        if (cleanUserName) {
            const byName = doctors.find(d => {
                const cleanDocName = normalize(d.label || d.name);
                return cleanDocName && (cleanDocName === cleanUserName || cleanDocName.includes(cleanUserName) || cleanUserName.includes(cleanDocName));
            });
            if (byName) return byName;
        }
    }

    // 5. If user role is doctor and only 1 doctor exists in clinic, return that doctor
    if (role === 'doctor' && doctors.length === 1) {
        return doctors[0];
    }

    return null;
};

/**
 * Convenience helper to get just the doctor_code (value) of the logged-in doctor
 */
export const getLoggedInDoctorCode = (doctors = []) => {
    const doc = getLoggedInDoctorOption(doctors);
    return doc?.value || null;
};

